import { useEffect, useRef } from "react";
import * as THREE from "three";

export const ZONE_HEX = { good: 0x34d399, amber: 0xfbbf24, red: 0xf87171, grey: 0x64748b };
const CAR_HEX = [0x22d3ee, 0xa78bfa];
const MERGE_S = 1.5;
const COLOUR_S = 0.8;

const PARTS = [
  { zone: "body", g: () => new THREE.BoxGeometry(4.2, 0.7, 1.8), p: [0, 0.75, 0] },
  { zone: "body", g: () => new THREE.BoxGeometry(2.2, 0.6, 1.5), p: [-0.25, 1.4, 0] },
  { zone: "engine", g: () => new THREE.BoxGeometry(0.9, 0.45, 0.9), p: [1.4, 0.72, 0] },
  { zone: "engine", g: () => new THREE.BoxGeometry(0.5, 0.35, 0.5), p: [0.7, 0.6, 0] },
  { zone: "running", g: () => new THREE.CylinderGeometry(0.07, 0.07, 1.3, 10), p: [-1.75, 0.32, 0.5], r: [0, 0, Math.PI / 2] },
  { zone: "history", g: () => new THREE.BoxGeometry(0.25, 0.22, 1.2), p: [0.72, 1.18, 0] },
  ...[[1.35, 0.9], [1.35, -0.9], [-1.35, 0.9], [-1.35, -0.9]].flatMap(([x, z]) => [
    { zone: "tyres", g: () => new THREE.TorusGeometry(0.36, 0.13, 10, 28), p: [x, 0.42, z] },
    { zone: "brakes", g: () => new THREE.CylinderGeometry(0.21, 0.21, 0.04, 20), p: [x, 0.42, z * 0.86], r: [Math.PI / 2, 0, 0] },
    { zone: "suspension", g: () => new THREE.CylinderGeometry(0.045, 0.045, 0.6, 8), p: [x, 0.88, z * 0.75] },
  ]),
];

function buildCar(hex) {
  const group = new THREE.Group();
  const mats = [];
  PARTS.forEach((part) => {
    const geom = part.g();
    const fill = new THREE.Mesh(geom, new THREE.MeshBasicMaterial({ color: hex, transparent: true, opacity: 0.08, blending: THREE.AdditiveBlending, depthWrite: false }));
    const line = new THREE.LineSegments(new THREE.EdgesGeometry(geom, 20), new THREE.LineBasicMaterial({ color: hex, transparent: true, opacity: 0.9, blending: THREE.AdditiveBlending }));
    [fill, line].forEach((o) => { o.position.set(...part.p); if (part.r) o.rotation.set(...part.r); o.userData.zone = part.zone; group.add(o); });
    mats.push({ zone: part.zone, fill: fill.material, line: line.material });
  });
  return { group, mats };
}

function buildFloor(scene) {
  const rings = [1.6, 2.6, 3.6, 4.6].map((r, i) => {
    const m = new THREE.Mesh(new THREE.RingGeometry(r, r + 0.03, 96), new THREE.MeshBasicMaterial({ color: 0x22d3ee, transparent: true, opacity: 0.35 - i * 0.07, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, depthWrite: false }));
    m.rotation.x = -Math.PI / 2;
    scene.add(m);
    return m;
  });
  return rings;
}

const ease = (x) => 1 - Math.pow(1 - x, 3);
const clamp = (x) => Math.min(1, Math.max(0, x));

export function hasWebGL() {
  try { const c = document.createElement("canvas"); return !!(window.WebGLRenderingContext && (c.getContext("webgl2") || c.getContext("webgl"))); } catch { return false; }
}

export function HologramStage({ zones, view, selected, onSelect, replayKey, reduce, onFail }) {
  const mount = useRef(null);
  const state = useRef({ zones, view, selected, start: 0 });
  state.current.zones = zones;
  state.current.view = view;
  state.current.selected = selected;

  useEffect(() => { state.current.start = performance.now(); }, [replayKey]);

  useEffect(() => {
    const el = mount.current;
    let renderer;
    try { renderer = new THREE.WebGLRenderer({ antialias: true }); } catch { onFail?.(); return undefined; }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x07121c);
    el.appendChild(renderer.domElement);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
    camera.position.set(0, 2.3, 8.2);
    camera.lookAt(0, 0.75, 0);
    const cars = CAR_HEX.map((h) => buildCar(h));
    const merged = buildCar(0xbff4ff);
    [...cars, merged].forEach((c) => scene.add(c.group));
    const rings = buildFloor(scene);
    const tmp = new THREE.Color();
    const ray = new THREE.Raycaster();
    const ptr = new THREE.Vector2();
    let aspect = 1;
    state.current.start = performance.now();

    const resize = () => {
      const w = el.clientWidth, h = el.clientHeight;
      aspect = w / h;
      renderer.setSize(w, h);
      camera.aspect = aspect;
      camera.updateProjectionMatrix();
    };
    const ro = new ResizeObserver(resize);
    ro.observe(el);
    resize();

    const onDown = (e) => {
      const r = renderer.domElement.getBoundingClientRect();
      ptr.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
      ray.setFromCamera(ptr, camera);
      const hit = ray.intersectObjects(merged.group.children.filter((o) => o.isMesh), false)[0];
      if (hit) onSelect?.(hit.object.userData.zone);
    };
    renderer.domElement.addEventListener("pointerdown", onDown);

    let raf;
    const loop = (now) => {
      const { zones: z, view: v, selected: sel, start } = state.current;
      const t = reduce ? 10 : (now - start) / 1000;
      const p = ease(clamp(t / MERGE_S));
      const spread = Math.min(3.0, aspect * 1.55) * (1 - p);
      const fadeSep = 1 - clamp((t / MERGE_S - 0.6) / 0.4);
      const fadeM = 1 - fadeSep;
      const sway = reduce ? -0.45 : -0.45 + Math.sin(now / 2600) * 0.3;
      cars[0].group.position.x = -spread;
      cars[1].group.position.x = spread;
      [...cars, merged].forEach((c) => { c.group.rotation.y = sway; });
      cars.forEach((c) => c.mats.forEach((m) => { m.line.opacity = 0.9 * fadeSep; m.fill.opacity = 0.08 * fadeSep; }));
      const cp = clamp((t - MERGE_S) / COLOUR_S);
      merged.mats.forEach((m) => {
        const st = z?.[m.zone]?.status?.[v] || "grey";
        tmp.setHex(0xbff4ff).lerp(new THREE.Color(ZONE_HEX[st]), cp);
        m.line.color.copy(tmp);
        m.fill.color.copy(tmp);
        const isSel = sel === m.zone;
        const pulse = isSel && !reduce ? 0.5 + 0.5 * Math.sin(now / 180) : 1;
        m.line.opacity = fadeM * (sel && !isSel ? 0.35 : 1) * (st === "grey" ? 0.55 : 1);
        m.fill.opacity = fadeM * (isSel ? 0.12 + 0.2 * pulse : st === "good" ? 0.14 : 0.08);
      });
      rings.forEach((r, i) => { r.material.opacity = (0.3 - i * 0.06) * (reduce ? 1 : 0.7 + 0.3 * Math.sin(now / 700 + i)); });
      renderer.render(scene, camera);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      renderer.domElement.removeEventListener("pointerdown", onDown);
      scene.traverse((o) => { o.geometry?.dispose(); o.material?.dispose(); });
      renderer.dispose();
      el.removeChild(renderer.domElement);
    };
  }, [reduce, onSelect, onFail]);

  return <div ref={mount} className="absolute inset-0 cursor-pointer [&>canvas]:[filter:drop-shadow(0_0_6px_rgba(34,211,238,0.55))]" data-testid="hologram-canvas" />;
}
